import React from 'react';

const ArtemisInfo = () => {
  const uniqueFeatures = [
    {
      icon: '🧬',
      title: 'Self-Improving AI',
      description: 'Combines LLMs with evolutionary algorithms - suggestions get better over time'
    },
    {
      icon: '📊',
      title: 'Multi-Source Scoring',
      description: 'AI evaluation + system benchmarks + human feedback = best results'
    },
    {
      icon: '🎯',
      title: 'Multi-Objective Optimization',
      description: 'Balance performance vs memory vs readability - you choose the weights'
    },
    {
      icon: '🔄',
      title: 'Automatic Evolution',
      description: 'Best suggestions breed better suggestions - Darwin meets AI'
    },
    {
      icon: '🗂️',
      title: 'Repository-Wide Vision',
      description: 'Optimizes entire codebases, not just individual files'
    },
    {
      icon: '✅',
      title: 'Real Validation',
      description: 'Every suggestion is tested: compilation, unit tests, benchmarks'
    }
  ];

  const competitorComparison = [
    { feature: 'Multiple LLM Support', artemis: '✅', others: '❌' },
    { feature: 'Evolutionary Optimization', artemis: '✅', others: '❌' },
    { feature: 'Repository-Wide Analysis', artemis: '✅', others: '❌' },
    { feature: 'Multi-Objective Scoring', artemis: '✅', others: '❌' },
    { feature: 'Automatic PR Generation', artemis: '✅', others: '⚠️' },
    { feature: 'Self-Improving AI', artemis: '✅', others: '❌' },
  ];

  return (
    <div style={{
      height: '100vh',
      width: '100%',
      background: 'linear-gradient(135deg, #F8F9FA 0%, #E9ECEF 100%)',
      overflowY: 'auto',
      padding: '40px'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        <h1 style={{ 
          textAlign: 'center', 
          color: '#F4511E', 
          marginBottom: '40px',
          fontSize: '42px',
          fontWeight: '700',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif'
        }}>
          🚀 Why TurinTech Artemis?
        </h1>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
          gap: '30px',
          marginBottom: '40px'
        }}>
          {/* Problem We Solve */}
          <div style={{
            background: '#FFF3E0',
            padding: '25px',
            borderRadius: '15px',
            border: '2px solid #FFE0B2',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
          }}>
            <h2 style={{ color: '#E65100', margin: '0 0 15px 0', fontWeight: '600', fontSize: '24px' }}>
              The Problem We Solve
            </h2>
            <p style={{ margin: 0, fontSize: '16px', color: '#424242', lineHeight: '1.6' }}>
              <strong>"My code is slow, help me optimize it"</strong><br/><br/>
              Artemis automatically finds performance bottlenecks and generates 
              optimal solutions using evolutionary AI. Unlike traditional tools, 
              we don't just suggest fixes - we evolve them until they're perfect.
            </p>
          </div>

          {/* Key Innovation */}
          <div style={{
            background: '#E8F5E9',
            padding: '25px',
            borderRadius: '15px',
            border: '2px solid #C8E6C9',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
          }}>
            <h2 style={{ color: '#1B5E20', margin: '0 0 15px 0', fontWeight: '600', fontSize: '24px' }}>
              Our Key Innovation
            </h2>
            <p style={{ margin: 0, fontSize: '16px', color: '#424242', lineHeight: '1.6' }}>
              <strong>Evolutionary AI + Multiple LLMs</strong><br/><br/>
              We combine Darwin's evolution with modern AI. Multiple LLMs generate 
              diverse solutions, then we evolve and crossbreed the best ones 
              through generations until we find the optimal solution.
            </p>
          </div>
        </div>

        {/* Unique Features */}
        <div style={{
          background: 'white',
          padding: '35px',
          borderRadius: '20px',
          marginBottom: '40px',
          boxShadow: '0 6px 20px rgba(0,0,0,0.08)'
        }}>
          <h2 style={{ color: '#212529', margin: '0 0 30px 0', fontWeight: '600', fontSize: '28px' }}>
            ✨ What Makes Artemis Unique
          </h2>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '25px' 
          }}>
            {uniqueFeatures.map((feature, index) => (
              <div key={index} style={{
                display: 'flex',
                gap: '15px',
                alignItems: 'flex-start',
                padding: '20px',
                background: '#F8F9FA',
                borderRadius: '12px',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                <span style={{ fontSize: '28px' }}>{feature.icon}</span>
                <div>
                  <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#2E7D32' }}>
                    {feature.title}
                  </h3>
                  <p style={{ margin: 0, fontSize: '14px', color: '#616161', lineHeight: '1.5' }}>
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Comparison Table */}
        <div style={{
          background: 'white',
          padding: '35px',
          borderRadius: '20px',
          boxShadow: '0 6px 20px rgba(0,0,0,0.08)'
        }}>
          <h2 style={{ color: '#4A148C', margin: '0 0 25px 0', fontWeight: '600', fontSize: '28px' }}>
            📊 Artemis vs Traditional Code Optimization Tools
          </h2>
          <table style={{ 
            width: '100%', 
            fontSize: '16px',
            borderCollapse: 'collapse'
          }}>
            <thead>
              <tr style={{ background: '#F3E5F5' }}>
                <th style={{ 
                  textAlign: 'left', 
                  padding: '15px', 
                  fontWeight: '600',
                  color: '#4A148C',
                  borderBottom: '2px solid #E1BEE7'
                }}>
                  Feature
                </th>
                <th style={{ 
                  textAlign: 'center', 
                  padding: '15px', 
                  fontWeight: '600',
                  color: '#4A148C',
                  borderBottom: '2px solid #E1BEE7'
                }}>
                  Artemis
                </th>
                <th style={{ 
                  textAlign: 'center', 
                  padding: '15px', 
                  fontWeight: '600',
                  color: '#4A148C',
                  borderBottom: '2px solid #E1BEE7'
                }}>
                  Traditional Tools
                </th>
              </tr>
            </thead>
            <tbody>
              {competitorComparison.map((row, idx) => (
                <tr key={idx} style={{ 
                  borderBottom: '1px solid #E0E0E0',
                  transition: 'background 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#F8F9FA'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '15px', color: '#424242' }}>{row.feature}</td>
                  <td style={{ textAlign: 'center', padding: '15px', fontSize: '20px' }}>{row.artemis}</td>
                  <td style={{ textAlign: 'center', padding: '15px', fontSize: '20px' }}>{row.others}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Bottom CTA */}
        <div style={{
          textAlign: 'center',
          marginTop: '60px',
          marginBottom: '40px'
        }}>
          <p style={{
            fontSize: '20px',
            color: '#495057',
            fontStyle: 'italic',
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif'
          }}>
            "The only code optimization tool that gets smarter with every use"
          </p>
        </div>
      </div>
    </div>
  );
};

export default ArtemisInfo;